/**
 * Props for UsageProgressBar component
 */
export interface UsageProgressBarProps {
  /**
   * Label for the usage metric
   */
  label: string;

  /**
   * Current usage value
   */
  current: number;

  /**
   * Maximum limit (-1 or 0 for unlimited)
   */
  limit: number;

  /**
   * Optional calculated percentage (will be calculated if not provided)
   */
  percentage?: number;

  /**
   * Show percentage in text
   */
  showPercentage?: boolean;

  /**
   * Custom thresholds for color warnings
   */
  thresholds?: {
    warning: number; // Default: 75
    danger: number; // Default: 90
  };

  /**
   * Loading state
   */
  isLoading?: boolean;

  /**
   * Custom className
   */
  className?: string;
}

/**
 * UsageProgressBar Component
 *
 * Displays a progress bar showing resource usage against a limit.
 * Used to show plan limits like campaign count, request count, team members, etc.
 * Color changes based on usage percentage: green (< 75%), yellow (75-89%), red (>= 90%).
 *
 * @example
 * ```tsx
 * <UsageProgressBar
 *   label="Campaigns"
 *   current={7}
 *   limit={10}
 * />
 * ```
 */
export function UsageProgressBar({
  label,
  current,
  limit,
  percentage: providedPercentage,
  showPercentage = true,
  thresholds = { warning: 75, danger: 90 },
  isLoading = false,
  className = '',
}: UsageProgressBarProps) {
  const isUnlimited = limit === -1 || limit === 0;
  const displayLimit = isUnlimited ? '∞' : limit.toLocaleString();

  // Calculate percentage if not provided
  const calculatedPercentage = isUnlimited
    ? 0
    : providedPercentage ?? (limit > 0 ? (current / limit) * 100 : 0);
  const displayPercentage = Math.min(calculatedPercentage, 100);

  // Determine color based on usage
  const getColor = (): string => {
    if (isUnlimited) return 'bg-blue-600';
    if (displayPercentage >= thresholds.danger) return 'bg-red-600';
    if (displayPercentage >= thresholds.warning) return 'bg-yellow-600';
    return 'bg-green-600';
  };

  // Get text color for percentage
  const getTextColor = (): string => {
    if (isUnlimited) return 'text-blue-600';
    if (displayPercentage >= thresholds.danger) return 'text-red-600';
    if (displayPercentage >= thresholds.warning) return 'text-yellow-600';
    return 'text-gray-600';
  };

  if (isLoading) {
    return (
      <div className={`space-y-2 ${className}`} data-testid="usage-bar-loading">
        <div className="animate-pulse">
          <div className="flex items-center justify-between mb-2">
            <div className="h-4 bg-gray-200 rounded w-24"></div>
            <div className="h-4 bg-gray-200 rounded w-20"></div>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2.5"></div>
        </div>
      </div>
    );
  }

  return (
    <div className={`space-y-2 ${className}`} data-testid="usage-bar">
      <div className="flex items-center justify-between text-sm">
        <span className="font-medium text-gray-700" data-testid="usage-bar-label">
          {label}
        </span>
        <span className={getTextColor()} data-testid="usage-bar-values">
          {current.toLocaleString()} / {displayLimit}
          {showPercentage && !isUnlimited && ` (${displayPercentage.toFixed(1)}%)`}
        </span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2.5" data-testid="usage-bar-track">
        <div
          className={`h-2.5 rounded-full transition-all duration-300 ${getColor()}`}
          style={{ width: `${isUnlimited ? 20 : displayPercentage}%` }}
          data-testid="usage-bar-fill"
          role="progressbar"
          aria-valuenow={current}
          aria-valuemin={0}
          aria-valuemax={isUnlimited ? undefined : limit}
          aria-label={`${label}: ${current} of ${displayLimit}`}
        ></div>
      </div>
    </div>
  );
}

export default UsageProgressBar;
